import { useEffect, useId, useRef, type ReactNode } from "react";

// Schedule a shift wizard chrome (Wave 2 design board frames 02 to 05 and 08, Implement
// rules 1, 2, 3 and 8). Slice A ships the SHELL only: the stepper, the footer CTA
// hierarchy, the neutral-disabled recipe, and the two-column body that folds at 1280.
// Step bodies arrive as content props so the connected slice can drop the real fields and
// hosts in without restructuring this chrome.
//
// Two rules here are review-blocking if broken, so they live in code rather than in a
// reviewer's memory:
//
//   1. One blue control per surface. The footer renders exactly one primary; Save draft
//      and Back are ghosts, Cancel is quiet text.
//   2. A primary that cannot run is NEUTRAL, never blue-but-inert, and carries exactly one
//      plain reason line beside it (board rule 3). `primary.blockedReason` is the only way
//      to disable it, which makes a silent disabled button unrepresentable.
//
// Green appears in one place only: the step 4 node, and only once the caller reports the
// published step. Progress is not proof, so steps 1 to 3 complete in blue.

export const SCHEDULE_WIZARD_STEPS = [
  { key: "define", label: "Define shift" },
  { key: "week", label: "Choose week" },
  { key: "review", label: "Review" },
  { key: "published", label: "Publish" },
] as const;

export type ScheduleWizardStepKey = (typeof SCHEDULE_WIZARD_STEPS)[number]["key"];

/** Step order index, used for done/current/upcoming state and Back targeting. */
export function scheduleWizardStepIndex(step: ScheduleWizardStepKey): number {
  return SCHEDULE_WIZARD_STEPS.findIndex((entry) => entry.key === step);
}

/** The step Back returns to, or null on the first step and after publish. */
export function scheduleWizardBackStep(step: ScheduleWizardStepKey): ScheduleWizardStepKey | null {
  if (step === "define" || step === "published") {
    return null;
  }
  return SCHEDULE_WIZARD_STEPS[scheduleWizardStepIndex(step) - 1]?.key ?? null;
}

export interface ScheduleWizardPrimary {
  readonly label: string;
  readonly onClick: () => void;
  /**
   * When set the control renders neutral and this exact sentence shows beside it. Absent
   * means the action can run, so the control is blue. There is deliberately no separate
   * `disabled` flag: a disabled control without a reason cannot be expressed.
   */
  readonly blockedReason?: string;
  /**
   * Optional ghost that clears the block, e.g. Choose week when no week is picked
   * (board rule 3: "a ghost button that fixes the block whenever one exists").
   */
  readonly repair?: { readonly label: string; readonly onClick: () => void };
  /** In flight: the control stays neutral and unclickable without claiming a block. */
  readonly busy?: boolean;
}

export function ScheduleWizard({
  step,
  title = "Schedule a shift",
  contextLabel,
  help,
  children,
  sideRail,
  primary,
  onBack,
  onCancel,
  onSaveDraft,
  footNote,
}: {
  readonly step: ScheduleWizardStepKey;
  /** Heading for the flow; the step name rides the stepper, not the title. */
  readonly title?: string;
  /** Quiet chip at the end of the stepper row, e.g. "Harborview nights · Week of Aug 18". */
  readonly contextLabel?: string;
  /** One plain line under the stepper telling the operator what this step is for. */
  readonly help?: string;
  /** The step body. Slice A ships the frame; the connected slice fills it. */
  readonly children: ReactNode;
  /** Right rail on wide desks; folds under the body at laptop widths (frame 08). */
  readonly sideRail?: ReactNode;
  /** The one blue action. Omitted on the published step, which offers its own next acts. */
  readonly primary?: ScheduleWizardPrimary;
  readonly onBack?: () => void;
  readonly onCancel?: () => void;
  /** Secondary exit, offered on Define and Choose week only (board rule 5). */
  readonly onSaveDraft?: () => void;
  /** Quiet line in the footer, used by the published step instead of buttons. */
  readonly footNote?: string;
}) {
  const reasonId = useId();
  const headingId = useId();
  const currentIndex = scheduleWizardStepIndex(step);
  const published = step === "published";
  const backStep = scheduleWizardBackStep(step);
  const showBack = backStep !== null && onBack !== undefined;
  // A block needs WORDS, not merely a defined field. Treating a blank reason as a block
  // would reintroduce exactly what rule 3 forbids: a dead control with nothing to read.
  // A caller that supplies no words has stated no reason, so the control stays runnable.
  const blockedReason = primary?.blockedReason?.trim();
  const blocked = blockedReason !== undefined && blockedReason.length > 0;
  const busy = primary?.busy === true;

  // Board rule 8: focus moves to the proof when publish returns, so a screen reader lands
  // on the recorded fact rather than being left on a control that has done its job.
  const proofRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (published) {
      proofRef.current?.focus();
    }
  }, [published]);

  return (
    <section className="schedwiz" aria-labelledby={headingId}>
      <header className="schedwiz__head">
        <h2 id={headingId} className="schedwiz__title">
          {title}
        </h2>
        <ol className="schedwiz__steps" aria-label={`${title} progress`}>
          {SCHEDULE_WIZARD_STEPS.map((entry, index) => {
            const done = index < currentIndex;
            const current = index === currentIndex;
            // The single green node in the whole flow, and only after the publish result.
            const proof = published && entry.key === "published";
            const state = proof ? "proof" : done ? "done" : current ? "current" : "upcoming";
            return (
              <li
                key={entry.key}
                className={`schedwiz__step schedwiz__step--${state}`}
                aria-current={current ? "step" : undefined}
              >
                <span className="schedwiz__step-node" aria-hidden="true">
                  {done || proof ? "✓" : index + 1}
                </span>
                <span className="schedwiz__step-label">
                  {proof ? "Published" : entry.label}
                </span>
              </li>
            );
          })}
        </ol>
        {contextLabel !== undefined ? (
          <span className="schedwiz__context">{contextLabel}</span>
        ) : null}
      </header>

      {help !== undefined ? <p className="schedwiz__help">{help}</p> : null}

      <div className={sideRail !== undefined ? "schedwiz__body schedwiz__body--railed" : "schedwiz__body"}>
        <div className="schedwiz__main">
          {published ? (
            // Focusable only as a programmatic target: it never enters the tab order.
            <div ref={proofRef} tabIndex={-1} className="schedwiz__proof-anchor">
              {children}
            </div>
          ) : (
            children
          )}
        </div>
        {sideRail !== undefined ? <aside className="schedwiz__side">{sideRail}</aside> : null}
      </div>

      <footer className="schedwiz__foot">
        {onCancel !== undefined && !published ? (
          <button type="button" className="schedwiz__quiet" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        {footNote !== undefined ? <p className="schedwiz__footnote">{footNote}</p> : null}
        <div className="schedwiz__foot-actions">
          {blocked ? (
            <p id={reasonId} className="schedwiz__reason">
              {blockedReason}
            </p>
          ) : null}
          {blocked && primary?.repair !== undefined ? (
            <button type="button" className="schedwiz__ghost" onClick={primary.repair.onClick}>
              {primary.repair.label}
            </button>
          ) : null}
          {onSaveDraft !== undefined && (step === "define" || step === "week") ? (
            <button type="button" className="schedwiz__ghost" onClick={onSaveDraft}>
              Save draft
            </button>
          ) : null}
          {showBack ? (
            <button type="button" className="schedwiz__ghost" onClick={onBack}>
              Back
            </button>
          ) : null}
          {primary !== undefined ? (
            <button
              type="button"
              className={blocked || busy ? "schedwiz__primary schedwiz__primary--blocked" : "schedwiz__primary"}
              onClick={primary.onClick}
              disabled={blocked || busy}
              aria-describedby={blocked ? reasonId : undefined}
            >
              {primary.label}
            </button>
          ) : null}
        </div>
      </footer>
    </section>
  );
}
